import type { CRMAccount, CRMContact, CRMOpportunity, OnboardingLead } from './crm';

export type MeetingType = 'call' | 'video_call' | 'in_person' | 'other';
export type MeetingStatus = 'scheduled' | 'completed' | 'cancelled' | 'no_show';
export type MeetingDocumentType = 'transcript' | 'recording' | 'summary' | 'notes' | 'attachment';
export type NoteType = 'general' | 'call_note' | 'meeting_note' | 'follow_up' | 'reminder';

/**
 * Provenance d'une note :
 * - `manual` : créée par un humain dans l'app admin (default DB).
 * - `agent`  : créée automatiquement par un agent Otto via MCP/CLI (revue de prospect…).
 */
export type NoteSource = 'manual' | 'agent';

/** Axe de regroupement des notes dans la barre latérale (UI-only). */
export type NoteGroupBy =
  | 'none'
  | 'type'
  | 'account'
  | 'contact'
  | 'opportunity'
  | 'meeting'
  | 'lead';

export interface CRMMeeting {
  id: string;
  title: string;
  description?: string | null;
  meeting_type: MeetingType;
  scheduled_at?: string | null;
  duration_minutes?: number | null;
  location?: string | null;
  google_meet_link?: string | null;
  google_calendar_event_id?: string | null;
  status: MeetingStatus;
  account_id?: string | null;
  owner_id: string;
  created_at: string;
  updated_at: string;
  account?: CRMAccount | null;
  participants?: CRMMeetingParticipant[];
  documents?: CRMMeetingDocument[];
}

export interface CRMMeetingParticipant {
  id: string;
  meeting_id: string;
  contact_id: string;
  role: string;
  created_at: string;
  contact?: CRMContact;
}

export interface CRMMeetingDocument {
  id: string;
  meeting_id: string;
  document_type: MeetingDocumentType;
  title?: string | null;
  content?: string | null;
  file_url?: string | null;
  source: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface CRMNote {
  id: string;
  title?: string | null;
  content: string;
  note_type: NoteType;
  account_id?: string | null;
  contact_id?: string | null;
  meeting_id?: string | null;
  appointment_id?: string | null;
  opportunity_id?: string | null;
  lead_id?: string | null;
  tags: string[];
  is_pinned: boolean;
  owner_id: string;
  created_at: string;
  updated_at: string;
  source: NoteSource;
  account?: CRMAccount | null;
  contact?: CRMContact | null;
  meeting?: CRMMeeting | null;
  opportunity?: CRMOpportunity | null;
  lead?: OnboardingLead | null;
  /** RDV calendaire lié (jointure légère depuis `appointments`), pour grouper par RDV. */
  appointment?: NoteAppointmentRef | null;
}

/** Référence légère vers un RDV calendaire, jointe au note pour l'affichage/groupement. */
export interface NoteAppointmentRef {
  id: string;
  title: string | null;
  start_time: string | null;
}

export interface CreateNoteInput {
  title?: string;
  content: string;
  note_type?: NoteType;
  account_id?: string | null;
  contact_id?: string | null;
  meeting_id?: string | null;
  appointment_id?: string | null;
  opportunity_id?: string | null;
  lead_id?: string | null;
  tags?: string[];
  is_pinned?: boolean;
}

export interface UpdateNoteInput {
  title?: string | null;
  content?: string;
  note_type?: NoteType;
  account_id?: string | null;
  contact_id?: string | null;
  meeting_id?: string | null;
  appointment_id?: string | null;
  opportunity_id?: string | null;
  lead_id?: string | null;
  tags?: string[];
  is_pinned?: boolean;
}

export interface CreateMeetingInput {
  title: string;
  description?: string;
  meeting_type?: MeetingType;
  scheduled_at?: string;
  duration_minutes?: number;
  location?: string;
  google_meet_link?: string;
  account_id?: string | null;
  contact_ids?: string[];
}

export interface NoteFilters {
  search?: string;
  note_type?: NoteType | 'all';
  /** Provenance : `manual` (défaut) masque les notes des agents Otto, `all` montre tout. */
  source?: NoteSource | 'all';
  account_id?: string;
  contact_id?: string;
  is_pinned?: boolean;
  tags?: string[];
  date_from?: string;
  date_to?: string;
}

export const NOTE_TYPE_LABELS: Record<NoteType, string> = {
  general: 'Note generale',
  call_note: 'Note d\'appel',
  meeting_note: 'Note de reunion',
  follow_up: 'Suivi',
  reminder: 'Rappel',
};

export const NOTE_GROUP_BY_LABELS: Record<NoteGroupBy, string> = {
  none: 'Aucun',
  type: 'Type',
  account: 'Société',
  contact: 'Contact',
  opportunity: 'Opportunité',
  meeting: 'RDV',
  lead: 'Lead',
};

export const NOTE_TYPE_COLORS: Record<NoteType, string> = {
  general: 'bg-neutral-100 text-neutral-700',
  call_note: 'bg-blue-100 text-[#0066FF]',
  meeting_note: 'bg-emerald-100 text-emerald-700',
  follow_up: 'bg-amber-100 text-amber-700',
  reminder: 'bg-red-100 text-red-700',
};

export const MEETING_TYPE_LABELS: Record<MeetingType, string> = {
  call: 'Appel',
  video_call: 'Visio',
  in_person: 'En personne',
  other: 'Autre',
};

export const MEETING_STATUS_LABELS: Record<MeetingStatus, string> = {
  scheduled: 'Planifie',
  completed: 'Termine',
  cancelled: 'Annule',
  no_show: 'Absent',
};

export interface CRMNoteHistoryEntry {
  id: string;
  note_id?: string;
  version_number: number;
  title: string | null;
  content: string;
  content_preview?: string;
  metadata: {
    note_type?: NoteType;
    tags?: string[];
    is_pinned?: boolean;
    account_id?: string | null;
    contact_id?: string | null;
    opportunity_id?: string | null;
  };
  created_by: string | null;
  created_at: string;
  change_reason: string | null;
}

export interface RestoreNoteVersionResult {
  success: boolean;
  error?: string;
  note?: CRMNote;
  restored_from_version?: number;
}
